import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { useCare } from '../../contexts/CareContext'
import { useToast, humanizeError } from '../shared/Toast'
import InvitePeople from '../profile/InvitePeople'
import TaskTemplates from './TaskTemplates'
import { todayISO } from '../../lib/tasks'

const ROLE_LABEL = {
  admin: 'Admin', family_member: 'Family', caregiver: 'Caregiver', care_recipient: 'Recipient',
}

/**
 * Everything a coordinator manages for one client, in one sheet — team,
 * recurring schedule, care plan, and removal. Kept separate from the family's
 * profile view so admin controls never clutter it.
 */
export default function AdminClientPanel({ onClose }) {
  const { user, profile } = useAuth()
  const { recipient, reload, setActiveId, memberships } = useCare()
  const toast = useToast()
  const navigate = useNavigate()

  const [team, setTeam] = useState([])
  const [todayTasks, setTodayTasks] = useState([])
  const [loading, setLoading] = useState(true)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [removing, setRemoving] = useState(false)

  const name = recipient?.preferred_name || recipient?.full_name || 'this client'

  const load = useCallback(async () => {
    if (!recipient) return
    const [teamRes, taskRes] = await Promise.all([
      supabase.from('care_team_members')
        .select('id, role, relationship, user_id, invited_name, invited_email, profile:profiles(full_name, email)')
        .eq('care_recipient_id', recipient.id).eq('is_active', true),
      supabase.from('tasks').select('*')
        .eq('care_recipient_id', recipient.id).eq('due_date', todayISO())
        .order('created_at', { ascending: true }),
    ])
    setTeam(teamRes.data || [])
    setTodayTasks(taskRes.data || [])
    setLoading(false)
  }, [recipient])

  useEffect(() => { load() }, [load])

  async function toggleTask(t) {
    const next = !t.is_completed
    setTodayTasks((l) => l.map((x) => (x.id === t.id ? { ...x, is_completed: next } : x)))
    const { error } = await supabase.from('tasks').update({
      is_completed: next,
      completed_at: next ? new Date().toISOString() : null,
      completed_by: next ? user.id : null,
    }).eq('id', t.id)
    if (error) {
      setTodayTasks((l) => l.map((x) => (x.id === t.id ? { ...x, is_completed: !next } : x)))
      toast.error(humanizeError(error))
    }
  }

  async function removeMember(m) {
    if (m.user_id === user.id) return toast.error("You can't remove yourself.")
    if (!window.confirm(`Remove ${m.profile?.full_name || m.invited_name || 'this member'}?`)) return
    const { error } = await supabase.from('care_team_members').update({ is_active: false }).eq('id', m.id)
    if (error) return toast.error(humanizeError(error))
    setTeam((t) => t.filter((x) => x.id !== m.id))
  }

  /**
   * Deleting the care_recipient cascades to every child table, so this is
   * gated behind typing the client's name rather than a single confirm.
   */
  async function removeClient() {
    const typed = window.prompt(
      `This permanently deletes ${name} and every care log, medication, document, and task for them. This cannot be undone.\n\nType the name to confirm:`
    )
    if (typed == null) return
    if (typed.trim().toLowerCase() !== (recipient.full_name || '').trim().toLowerCase()) {
      return toast.error('Name did not match — nothing was deleted.')
    }
    setRemoving(true)
    try {
      const { error } = await supabase.from('care_recipients').delete().eq('id', recipient.id)
      if (error) throw error
      const next = memberships.find((m) => m.recipient.id !== recipient.id)
      await reload()
      setActiveId(next ? next.recipient.id : null)
      toast.success(`${name} removed.`)
      onClose()
      navigate(next ? '/' : '/admin')
    } catch (err) {
      toast.error(humanizeError(err))
    } finally {
      setRemoving(false)
    }
  }

  if (!recipient) return null

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && !removing && onClose()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
    >
      <div style={{ background: 'var(--bg)', width: '100%', maxWidth: 'var(--maxw)', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 20, maxHeight: '92dvh', overflowY: 'auto' }}>
        <div className="spread" style={{ marginBottom: 12 }}>
          <h2 className="title" style={{ fontSize: 20 }}>Admin — {name}</h2>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--muted)' }}>✕</button>
        </div>

        {loading ? (
          <p className="muted" style={{ fontSize: 14 }}>Loading…</p>
        ) : (
          <div className="stack">
            {/* Care team */}
            <div className="card stack" style={{ gap: 10 }}>
              <div className="section-title">Care team</div>
              {team.length === 0 && (
                <p className="muted" style={{ fontSize: 14, margin: 0 }}>No one on the team yet.</p>
              )}
              {team.map((m) => (
                <div key={m.id} className="spread">
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>
                      {m.profile?.full_name || m.invited_name || m.invited_email || 'Pending invite'}
                      {!m.user_id && <span className="muted"> (invited)</span>}
                    </div>
                    <div className="muted" style={{ fontSize: 13 }}>{m.relationship || ROLE_LABEL[m.role] || m.role}</div>
                  </div>
                  <div className="row" style={{ gap: 10, flexShrink: 0 }}>
                    <span className="badge">{ROLE_LABEL[m.role] || m.role}</span>
                    {m.user_id !== user.id && (
                      <button onClick={() => removeMember(m)} style={{ background: 'none', border: 'none', color: 'var(--danger)', fontSize: 13 }}>Remove</button>
                    )}
                  </div>
                </div>
              ))}
              <button className="btn btn-ghost btn-block" onClick={() => setInviteOpen(true)}>+ Invite member</button>
            </div>

            {/* Recurring schedule */}
            <TaskTemplates recipientId={recipient.id} canManage />

            {/* Today's generated tasks */}
            <div className="card stack" style={{ gap: 8 }}>
              <div className="section-title">Today&rsquo;s tasks</div>
              {todayTasks.length === 0 ? (
                <p className="muted" style={{ fontSize: 14, margin: 0 }}>Nothing scheduled for today.</p>
              ) : (
                todayTasks.map((t) => (
                  <label key={t.id} className="row" style={{ gap: 10, cursor: 'pointer', minHeight: 40 }}>
                    <input
                      type="checkbox" checked={!!t.is_completed}
                      onChange={() => toggleTask(t)}
                      style={{ width: 20, height: 20, flexShrink: 0 }}
                    />
                    <span
                      className="grow"
                      style={{
                        textDecoration: t.is_completed ? 'line-through' : 'none',
                        opacity: t.is_completed ? 0.55 : 1,
                      }}
                    >
                      {t.title}
                    </span>
                  </label>
                ))
              )}
              <p className="muted" style={{ fontSize: 12, margin: 0 }}>
                You can tick these off on a caregiver&rsquo;s behalf.
              </p>
            </div>

            {/* Care plan */}
            <div className="card stack" style={{ gap: 8 }}>
              <div className="section-title">Care plan</div>
              <button className="btn btn-secondary btn-block" onClick={() => { onClose(); navigate('/care-plan-actions') }}>
                Import or edit care plan →
              </button>
              <p className="muted" style={{ fontSize: 12, margin: 0 }}>
                Import an existing plan and edit goals from the Care plan screen.
              </p>
            </div>

            {/* Danger zone */}
            <div className="card stack" style={{ gap: 8, borderColor: 'var(--danger)' }}>
              <div className="section-title" style={{ color: 'var(--danger)' }}>Danger zone</div>
              <p className="muted" style={{ fontSize: 13, margin: 0, lineHeight: 1.55 }}>
                Removing {name} permanently deletes their care logs, medications,
                documents, appointments, and tasks. This cannot be undone.
              </p>
              <button
                className="btn btn-block"
                style={{ background: 'var(--danger)', borderColor: 'var(--danger)' }}
                onClick={removeClient}
                disabled={removing}
              >
                {removing ? <span className="spinner" /> : 'Remove this client'}
              </button>
            </div>

            <div style={{ height: 8 }} />
          </div>
        )}

        {inviteOpen && (
          <InvitePeople
            recipient={recipient}
            inviterName={profile?.full_name || 'A coordinator'}
            onCancel={() => setInviteOpen(false)}
            onDone={() => { setInviteOpen(false); load() }}
          />
        )}
      </div>
    </div>
  )
}
